import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OneSignal } from 'onesignal-ngx';
import { Observable, firstValueFrom, from, of, timer } from 'rxjs';
import { catchError, mapTo, switchMap, take } from 'rxjs/operators';
import { SuccessResponse } from 'src/app/account/services/account-api.service';
import { AuthenticationRepositoryService } from 'src/app/authentication/services/authentication-repository.service';
import { environment } from 'src/environments/environment';
import { USER_DATA_LOCAL_STORAGE } from '../../constants/local-storage-key.constants';
import { UserSession } from '../../models/user.model';
import { PlatformService } from '../platform/platform.service';
import { StorageService } from '../storage/storage.service';
import { UserService } from '../user/user.service';


export interface PushNotificationConfiguration {
  is_push_notification_enabled: boolean;
  app?: {
    id?: string;
    name: string;
  },
  prompt: PushNotificationPrompt;
}

export interface PushNotificationPrompt {
  sales_channel_configuration: string;
  prompt_title: string;
  prompt_message: string;
  prompt_accept_button_label: string;
  prompt_reject_button_label: string;
  send_subscribed_reply_notification: boolean;
  subscribed_reply_notification_title: string;
  subscribed_reply_notification_message: string;
  subscribed_reply_notification_url: string;
  prompt_delay_ms?: number;
}

export const DefaultPushNotificationPromptMessage = {
  prompt_title: "Receive push notifications to be updated to latest news?",
  prompt_message: "Allowing push notifications service will help you to get access to latest products information and product offers, new deals and much more.",
  prompt_accept_button_label: "Yes, Subscribe",
  prompt_reject_button_label: "Not now",
  subscribed_reply_notification_title: "Thank you for subscribing!!!",
  subscribed_reply_notification_message: "You will now receive notifications from us now.",
  prompt_delay_ms: 2000
}

export const PUSH_NOTIFICATION_DISABLED_DATE = 'push-notification-disabled-date';
export const ONESIGNAL_SUBSCRIPTION_ID = 'onesignal-subscription-id';
export const ACCESS_TOKEN = 'authorization-token';
const ONE_WEEK = 604800;

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private oneSignal = inject(OneSignal);
  private userService = inject(UserService);
  private platformService = inject(PlatformService);
  // private environmentService = inject(EnvironmentService);
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private authenticationRepositoryService = inject(AuthenticationRepositoryService);

  isPushSupported = false;
  isFirstLoad = true;

  marketingBaseUrl: string;
  version: string;

  private _oneSignalInitialized = signal<boolean | null>(null);
  private _createAnonymousSubscriber = signal<string | null>(null);
  private _createContactSubscriptionSignal = signal<string | null>(null);

  // Public readonly signals
  readonly oneSignalInitialized = this._oneSignalInitialized.asReadonly();
  readonly createAnonymousSubscriberSignal = this._createAnonymousSubscriber.asReadonly();
  readonly createContactSubscriptionSignal = this._createContactSubscriptionSignal.asReadonly();

  private _prompt = signal<PushNotificationPrompt | null>(null);
  readonly prompt = this._prompt.asReadonly();

  private _showPrompt = signal({ showPrompt: false, delay: 2000 });
  readonly showPrompt = this._showPrompt.asReadonly();

  // Computed signals for derived state
  readonly hasPrompt = computed(() => !!this._prompt());
  readonly shouldShowPrompt = computed(() => this._showPrompt().showPrompt);
  readonly promptDelay = computed(() => this._prompt()?.prompt_delay_ms || DefaultPushNotificationPromptMessage.prompt_delay_ms);

  // Computed signal with delay for showing prompt
  readonly shouldShowPromptDelayed = toSignal(
    toObservable(this.shouldShowPrompt).pipe(
      switchMap(shouldShow => shouldShow ? timer(this.promptDelay()).pipe(mapTo(true)) : of(false))
    ),
    { initialValue: false }
  );

  // Backward compatibility: Observable versions for existing code
  readonly oneSignalInitialized$ = toObservable(this.oneSignalInitialized);
  readonly createAnonymousSubscriber$ = toObservable(this.createAnonymousSubscriberSignal);
  readonly createContactSubscription$ = toObservable(this.createContactSubscriptionSignal);

  constructor() {
    this.marketingBaseUrl = environment.marketingUrl;
    this.version = environment.version1;

    effect(() => {
      const isLoggedIn = this.authenticationRepositoryService.isUserLoggedIn();
      if (!this.isFirstLoad && this.isPushSupported) {
        if (isLoggedIn) {
          this.managePushNotificationProfile(true);
        } else {
          this.managePushNotificationProfile(false);
        }
      }
      this.isFirstLoad = false;
    });

    effect(() => {
      const subscriptionId = this.createAnonymousSubscriberSignal();
      if (subscriptionId) {
        // Use async/await pattern instead of subscribe
        this.createAnonymousSubscriber(subscriptionId).pipe(
          catchError(() => of(null))
        ).subscribe({
          next: () => {
            // Handle success
          },
          error: (error) => {
            // Anonymous subscriber error
          }
        });
      }
    });

    effect(() => {
      const subscriptionId = this.createContactSubscriptionSignal();
      if (subscriptionId) {
        // Use async/await pattern instead of subscribe
        this.createContactSubscription(subscriptionId).pipe(
          catchError(() => of(null))
        ).subscribe({
          next: () => {
            // Handle success
          },
          error: (error) => {
            // Contact subscription error
          }
        });
      }
    });

    // Effect to handle oneSignalInitialized changes
    effect(() => {
      if (this.oneSignalInitialized()) {
        // Handle initialization logic here
        this.isPushSupported = this.oneSignal.Notifications.isPushSupported();

        if (!this.isPushSupported) {
          return;
        }

        const lastPushNotificationDisabledDate = JSON.parse(this.storageService.retrieve(PUSH_NOTIFICATION_DISABLED_DATE) || '0');
        const isPushNotificationDisableDateGreaterThanAWeek = (Date.parse(new Date().toISOString()) / 1000) - lastPushNotificationDisabledDate > ONE_WEEK;
        // Checking is user opted in for push notification or is it more than one week after user opted out from push-notification
        const isOptedIn = this.oneSignal.User.PushSubscription.optedIn || !isPushNotificationDisableDateGreaterThanAWeek;
        if (!isOptedIn) {
          const delay = this._prompt()?.prompt_delay_ms || DefaultPushNotificationPromptMessage.prompt_delay_ms;
          this._showPrompt.set({ showPrompt: true, delay });
        }

        // listening to notification pop-up change event
        this.oneSignal.User.PushSubscription.addEventListener('change', (event: any) => {
          const isLoggedIn = this.userService.appUser;
          const { current: { optedIn, id: subscriptionId } } = event || {};
          if (subscriptionId && optedIn) {
            if (!isLoggedIn) {
              this.setAnonymousSubscriber(subscriptionId);
            } else {
              this.setContactSubscription(subscriptionId);
            }
          }

          // unsubscribe
          if (!optedIn) {
          }
        });
      }
    });
  }

  // Initialize the service - now only handles non-effect initialization
  initializeService(): void {
  }



  initializePushNotifications() {
    if (this.platformService.isBrowser()) {
      // Checking is push notification enabled for given saleschannel
      this.getPushNotificationConfigurations().pipe(
        switchMap((pushNotificationConfiguration: any) => {
          const { is_push_notification_enabled } = pushNotificationConfiguration;
          // If yes initializing onesignal
          if (is_push_notification_enabled) {
            const { app, prompt } = pushNotificationConfiguration;
            if (prompt && Object.keys(prompt).length) {
              this._prompt.set(prompt);
            }
            return from(
              this.oneSignal.init({
                appId: app?.id ?? '',
                allowLocalhostAsSecureOrigin: true,
                autoResubscribe: false,
                welcomeNotification: {
                  disable: !prompt?.send_subscribed_reply_notification,
                  title: prompt?.subscribed_reply_notification_title ?? DefaultPushNotificationPromptMessage.subscribed_reply_notification_title,
                  message: prompt?.subscribed_reply_notification_message ?? DefaultPushNotificationPromptMessage.subscribed_reply_notification_message,
                  url: prompt?.subscribed_reply_notification_url
                }
              })
            ).pipe(mapTo(true))
          } else {
            return of(false)
          }
        })
      ).pipe(
        take(1)
      ).subscribe({
        next: (isInitialized: any) => {
          // Ã¢Å"â€¦ MIGRATED: Convert OneSignal initialization to signal-based pattern
          if (isInitialized) {
            this.setOneSignalInitialized();
          }
          // Checking is user is loggedin or not
          const userData: UserSession = this.storageService.retrieve(USER_DATA_LOCAL_STORAGE)
          if (isInitialized && userData) {
            const { user } = userData;
            if (user?.id) {
              this.oneSignal?.login(user.id);
            }
          }

          // Checking is user loggedout from saleschannel
          const onesignalSubscriptionId = this.storageService.retrieve(ONESIGNAL_SUBSCRIPTION_ID);
          if (onesignalSubscriptionId) {
            // If yes logging out from onesignal and hyper
            this.logoutFromOneSignal(onesignalSubscriptionId);
          }
        },
        error: (error) => {
          // OneSignal initialization error
        }
      });


      // Effects are now set up in constructor to avoid circular dependencies
    }
  }

  getPushNotificationConfigurations(): Observable<Partial<PushNotificationConfiguration>> {
    return this.http.get<Partial<PushNotificationConfiguration>>(`${this.marketingBaseUrl}/${this.version}/push_notification_configurations`).pipe(
      catchError(() => of({ is_push_notification_enabled: false }))
    )
  }

  createAnonymousSubscriber(subscriptionId: string): Observable<any> {
    return this.http.post(`${this.marketingBaseUrl}/${this.version}/consumer/anonymous_push_subscriber`, { subscription_id: subscriptionId });
  }

  createContactSubscription(subscriptionId: string): Observable<any> {
    return this.http.post(`${this.marketingBaseUrl}/${this.version}/consumer/contact_push_subscriber`, { subscription_id: subscriptionId });
  }

  managePushNotificationProfile(isLogged: boolean) {
    const subscription_id = this.oneSignal.User.PushSubscription.id;
    const userData: UserSession = this.storageService.retrieve(USER_DATA_LOCAL_STORAGE);
    if (subscription_id) {
      if (isLogged) {
        const oneSignalLogin = async () => {
          if (userData?.user?.id) {
            await this.oneSignal?.login(userData.user.id);
          }
          return this.loginToPushNotification();
        }
        oneSignalLogin().then(() => { });
      } else {
        // Storing subscription_id and access_token to make push-notification logout call after logout and it is authenticated call
        this.storageService.save(ONESIGNAL_SUBSCRIPTION_ID, subscription_id);
        this.storageService.save(ACCESS_TOKEN, userData?.access_token);
      }
    }
  }

  logoutFromOneSignal(subscriptionId: string) {

    const oneSignalLogout = async () => {
      await this.oneSignal?.logout();
      return firstValueFrom(this.loginOutFromPushNotification(subscriptionId));
    }

    oneSignalLogout().then(() => { }
    ).finally(() => {
      // Removing subscriptionId and access token from localstorage.
      this.storageService.remove(ONESIGNAL_SUBSCRIPTION_ID);
      this.storageService.remove(ACCESS_TOKEN);
    });
  }

  private async loginToPushNotification() {
    const isInitialized = this.oneSignal?.isInitialized;
    if (isInitialized) {
      const userData: UserSession = this.storageService.retrieve(USER_DATA_LOCAL_STORAGE)
      if (isInitialized && userData) {
        const { user } = userData;
        if (user?.id) {
          await this.oneSignal?.login(user.id);
        }
      }
    }
  }

  loginOutFromPushNotification(subscription_id: string): Observable<any> {
    return this.http.post<SuccessResponse>(`${this.marketingBaseUrl}/${this.version}/consumer/push_notifications_logout`, { subscription_id });
  }

  public subscribe() {
    this._showPrompt.set({ showPrompt: false, delay: 0 });
    this.oneSignal.User.PushSubscription.optIn();
  }

  public unsubscribe() {
    this._showPrompt.set({ showPrompt: false, delay: 0 });
    this.oneSignal.User.PushSubscription.optOut();
    this.storageService.save(PUSH_NOTIFICATION_DISABLED_DATE, Date.parse(new Date().toISOString()) / 1000);
  }

  /**
   * Set one signal initialized
   */
  setOneSignalInitialized(): void {
    // Set to true to trigger the effect that handles OneSignal initialization
    this._oneSignalInitialized.set(true);
  }

  /**
   * Set anonymous subscriber
   */
  setAnonymousSubscriber(subscriptionId: string): void {
    this._createAnonymousSubscriber.set(subscriptionId);
  }

  /**
   * Set contact subscription
   */
  setContactSubscription(subscriptionId: string): void {
    this._createContactSubscriptionSignal.set(subscriptionId);
  }
}
